import React from "react";
import { Box, FormControlLabel } from "@mui/material";
import CustomSelect, { ICustomSelectProps } from "./CustomSelect";
import CustomButton, { ICustomButtonProps } from "./CustomButton";
import { COLORS } from "../../assets/theme";
import CustomTextField, { ICustomTextFieldProps } from "./CustomTextField";
import CustomRadio from "../CustomRadio";
import CustomCheckbox from "../CustomCheckbox";
import type { IAutoCompleteProps } from "./AutoComplete";
import AutoComplete from "./AutoComplete";

type ResponsiveSize = {
  xs?: number | string;
  sm?: number | string;
  md?: number | string;
  lg?: number | string;
};

const DEFAULT_FIELD_BASIS: Required<ResponsiveSize> = {
  xs: "100%",
  sm: "calc(50% - 8px)",
  md: "calc(33.333% - 11px)",
  lg: "calc(25% - 12px)",
};

const getFlexBasis = (size?: ResponsiveSize) => ({
  xs: size?.xs ?? DEFAULT_FIELD_BASIS.xs,
  sm: size?.sm ?? DEFAULT_FIELD_BASIS.sm,
  md: size?.md ?? DEFAULT_FIELD_BASIS.md,
  lg: size?.lg ?? DEFAULT_FIELD_BASIS.lg,
});

type ISearchFormProps = {
  textFields?: (ICustomTextFieldProps & { size?: ResponsiveSize })[];
  dropdowns?: (ICustomSelectProps & { size?: ResponsiveSize })[];
  buttons?: (ICustomButtonProps & { sx?: object; disabled?: boolean })[];
  radioButtons?: {
    name: string;
    value: string;
    selectedValue: string;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  }[];
  checkboxes?: {
    label: string;
    value: boolean;
    checked: boolean;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    ariaLabel?: string;
  }[];
  dateComponent?: React.ReactNode;
  BoxSizes?: {
    textField?: ResponsiveSize;
    dropdown?: ResponsiveSize;
    buttons?: ResponsiveSize;
    checkboxes?: ResponsiveSize;
  };
  rpdid?: string;
  children?: React.ReactNode;
  autoCompletes?: (IAutoCompleteProps & { size?: ResponsiveSize })[];
};

const SearchForm: React.FC<ISearchFormProps> = ({
  textFields = [],
  dropdowns = [],
  buttons = [],
  radioButtons = [],
  checkboxes = [],
  dateComponent,
  rpdid,
  children,
  autoCompletes = [],
}) => {
  const renderDropdowns = () =>
    dropdowns.map((dropdown, index) => (
      <Box
        key={`dropdown-${index}`}
        sx={{
          flexBasis: getFlexBasis(dropdown.size),
          maxWidth: "100%",
        }}
      >
        <CustomSelect
          {...dropdown}
          placeholder={dropdown.placeholder || "Select an option"}
          size="small"
          isDeleted={dropdown.isDeleted}
          onToggleDeleted={dropdown.onToggleDeleted}
          fullWidth={rpdid !== "RPD"}
          multiSelect={dropdown.multiSelect}
          bgColor={COLORS.iconColor}
          renderPlaceholder
        />
      </Box>
    ));

  const renderTextFields = () =>
    textFields.map((field, index) => (
      <Box
        key={`textfield-${index}`}
        sx={{
          flexBasis: getFlexBasis(field.size),
        }}
      >
        <CustomTextField
          {...field}
          size="small"
          bgColor={field.bgColor || COLORS.iconColor}
        />
      </Box>
    ));

  const renderAutoCompletes = () =>
    autoCompletes.map((auto, index) => (
      <Box
        key={`autocomplete-${index}`}
        sx={{
          flexBasis: getFlexBasis(auto.size),
        }}
      >
        <AutoComplete
          {...auto}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: COLORS.iconColor,
            },
          }}
        />
      </Box>
    ));

  const renderRadioAndCheckboxes = () => (
    <Box
      display="flex"
      flexWrap="nowrap"
      justifyContent={{ xs: "space-between", sm: "flex-start" }}
      gap={{ xs: 0.5, sm: 2 }}
      marginTop={{ xs: "6px", sm: "20px" }}
      alignItems="center"
      width={{ xs: "100%", sm: "auto" }}
      sx={{
        "& .MuiFormControlLabel-root": {
          marginLeft: 0,
          marginRight: { xs: 0, sm: 1 },
          gap: { xs: "2px", sm: "4px" },
          whiteSpace: "nowrap",
        },
        "& .MuiRadio-root, & .MuiCheckbox-root": {
          padding: { xs: "4px", sm: "9px" },
        },
        "& .MuiSvgIcon-root": {
          fontSize: { xs: 20, sm: 24 },
        },
        "& .MuiFormControlLabel-label, & .MuiTypography-root": {
          fontSize: { xs: "0.8rem", sm: "1rem" },
          whiteSpace: "nowrap",
        },
      }}
    >
      {radioButtons.map((radio, index) => (
        <Box
          key={`radio-${index}`}
          sx={{
            flex: { xs: "1 1 0", sm: "0 0 auto" },
            display: "flex",
            justifyContent: "center",
          }}
        >
          <FormControlLabel
            control={
              <CustomRadio
                checked={radio.selectedValue === radio.value}
                onChange={radio.onChange}
                value={radio.value}
              />
            }
            label={radio.name}
          />
        </Box>
      ))}
      {checkboxes.map((checkbox, idx) => (
        <Box
          key={`checkbox-${idx}`}
          sx={{
            flex: { xs: "1 1 0", sm: "0 0 auto" },
            display: "flex",
            justifyContent: "center",
          }}
        >
          <CustomCheckbox
            checked={checkbox.checked}
            onChange={checkbox.onChange}
            label={checkbox.label}
            ariaLabel={checkbox.ariaLabel}
          />
        </Box>
      ))}
    </Box>
  );

  const renderButtons = () =>
    buttons.length > 0 && (
      <Box
        display="flex"
        justifyContent="flex-end"
        gap={2}
        flexWrap="wrap"
        sx={{ flexGrow: 1, flexShrink: 1 }}
      >
        {buttons.map((button, index) => (
          <CustomButton
            key={`button-${index}`}
            {...button}
            disabled={button.disabled || false}
            sx={{
              "&:hover": {
                backgroundColor: button.bgColor || COLORS.GOLD,
              },
              ...button.sx,
            }}
          />
        ))}
      </Box>
    );

  return (
    <Box
      padding={{ xs: 1, sm: 3 }}
      borderRadius={2}
      marginBottom={1}
      sx={{
        backgroundColor: COLORS.iconColor,
        overflowX: "hidden",
        maxHeight: { xs: "unset", sm: "50vh" },
        overflowY: "auto",
        width: "100%",
      }}
    >
      {dateComponent && (
        <Box
          display="flex"
          justifyContent="flex-start"
          marginBottom={{ xs: 1, sm: 2 }}
        >
          {dateComponent}
        </Box>
      )}
      <Box display="flex" flexDirection="column" gap={{ xs: 1.5, sm: 3 }}>
        {rpdid === "RPD" ? (
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            gap={{ xs: 1, sm: 0 }}
          >
            <Box flex="2" width={{ xs: "100%", sm: "auto" }}>
              <Box display="flex" flexWrap="wrap" gap={{ xs: 1, sm: 2 }}>
                {autoCompletes.length > 0
                  ? renderAutoCompletes()
                  : renderDropdowns()}
              </Box>
              {children && (
                <Box width="100%" mt={{ xs: 1, sm: 2 }} mb={{ xs: 1, sm: 2 }}>
                  {children}
                </Box>
              )}
            </Box>
            <Box
              flex="2"
              mt={{ xs: 1, sm: 13 }}
              width={{ xs: "100%", sm: "auto" }}
            >
              {renderButtons()}
            </Box>
          </Box>
        ) : (
          <Box display="flex" gap={{ xs: 1, sm: 2 }} flexDirection="column">
            <Box
              display="flex"
              flexWrap="wrap"
              gap={{ xs: 1, sm: 2 }}
              flex="1"
              flexDirection={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "flex-start" }}
              width="100%"
            >
              {renderDropdowns()}
              {renderAutoCompletes()}
              {renderTextFields()}

              <Box sx={{ display: "flex", width: { xs: "100%", sm: "auto" } }}>
                {renderRadioAndCheckboxes()}
              </Box>
              {!children && (
                <Box
                  flex={1}
                  width={{ xs: "100%", sm: "auto" }}
                  mt={{ xs: 1, sm: 2 }}
                >
                  {renderButtons()}
                </Box>
              )}
            </Box>
            {children && (
              <Box
                width="100%"
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                gap={{ xs: 1, sm: 2 }}
              >
                <Box flex="2" width={{ xs: "100%", sm: "auto" }}>
                  {children}
                </Box>
                {renderButtons()}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SearchForm;
